﻿import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Page, Card, Button, Banner, Text, TextField,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, Box, Pagination, Tooltip, InlineGrid,
} from '@shopify/polaris';
import { DeleteIcon, DiscountIcon, CashDollarIcon, PlusIcon } from '@shopify/polaris-icons';
import { useBundles } from '../../hooks/useBundles';
import { StatCard } from '../shared/StatCard';
import { MIN_BUNDLE_PRODUCTS, MAX_BUNDLE_PRODUCTS } from '../../api/bundles';

interface BundlePrefill {
  productIds: string[];
  discountPercent: string;
  title: string;
}

export function BundleDiscountsPanel() {
  const {
    bundles, loading, creating, error, total, page, lastPage,
    fetchBundles, createBundle, deleteBundle, setError,
  } = useBundles();

  const avgDiscount = useMemo(
    () => (bundles.length === 0 ? 0 : Math.round(bundles.reduce((sum, b) => sum + b.discount_percent, 0) / bundles.length)),
    [bundles],
  );

  // Captured once on mount only — a merchant arriving from an Upsell
  // recommendation's "Create Bundle Discount" button gets the form
  // pre-filled for review, but navigating away and back shouldn't keep
  // re-applying stale prefill data from browser history state.
  const location = useLocation();
  const [prefill] = useState<BundlePrefill | null>(() => (location.state as { prefill?: BundlePrefill } | null)?.prefill ?? null);

  const [title, setTitle] = useState(prefill?.title ?? '');
  const [productIds, setProductIds] = useState<string[]>(prefill?.productIds ?? ['', '']);
  const [discountPercent, setDiscountPercent] = useState(prefill?.discountPercent ?? '15');
  const [showPrefillBanner, setShowPrefillBanner] = useState(prefill !== null);

  useEffect(() => { fetchBundles(1); }, [fetchBundles]);

  const filledProductIds = productIds.map((id) => id.trim()).filter((id) => id !== '');
  const canSubmit = title.trim() !== '' && filledProductIds.length >= MIN_BUNDLE_PRODUCTS;

  const updateProductId = (index: number, value: string) => {
    setProductIds((prev) => prev.map((id, i) => (i === index ? value : id)));
  };

  const addProductSlot = () => {
    setProductIds((prev) => (prev.length < MAX_BUNDLE_PRODUCTS ? [...prev, ''] : prev));
  };

  const removeProductSlot = (index: number) => {
    setProductIds((prev) => (prev.length > MIN_BUNDLE_PRODUCTS ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleCreate = async () => {
    const result = await createBundle({
      shopify_product_ids: filledProductIds.map(Number),
      discount_percent: Number(discountPercent),
      title,
    });
    if (result) {
      setTitle('');
      setProductIds(['', '']);
      setShowPrefillBanner(false);
    }
  };

  return (
    <Page fullWidth
      title="Bundling & Volume Discounts"
      subtitle="Create a real, redeemable Shopify discount code for two products sold as a bundle — not just a suggestion, this actually works at checkout"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {showPrefillBanner && (
            <Banner tone="info" onDismiss={() => setShowPrefillBanner(false)}>
              <p>Pre-filled from an Upsell recommendation &mdash; review the details below and create when ready.</p>
            </Banner>
          )}

          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            <StatCard label="Active Bundle Discounts" value={String(total)} icon={DiscountIcon} iconColor="purple" />
            <StatCard label="Average Discount" value={bundles.length > 0 ? `${avgDiscount}%` : '—'} icon={CashDollarIcon} iconColor="green" />
          </InlineGrid>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Create a Bundle Discount</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Combine {MIN_BUNDLE_PRODUCTS}-{MAX_BUNDLE_PRODUCTS} products into one bundle. You can find a
                product's ID in Shopify Admin's URL bar when editing that product (e.g. .../products/<strong>1234567890</strong>).
              </Text>

              <BlockStack gap="200">
                {productIds.map((id, index) => (
                  <InlineStack key={index} gap="200" blockAlign="end">
                    <Box minWidth="220px">
                      <TextField
                        label={`Product ${index + 1} ID`}
                        autoComplete="off"
                        value={id}
                        onChange={(v) => updateProductId(index, v)}
                      />
                    </Box>
                    {productIds.length > MIN_BUNDLE_PRODUCTS && (
                      <Button
                        icon={DeleteIcon}
                        accessibilityLabel={`Remove product ${index + 1}`}
                        onClick={() => removeProductSlot(index)}
                      />
                    )}
                  </InlineStack>
                ))}

                {productIds.length < MAX_BUNDLE_PRODUCTS && (
                  <Box>
                    <Button icon={PlusIcon} onClick={addProductSlot}>Add another product</Button>
                  </Box>
                )}
              </BlockStack>

              <InlineStack gap="300">
                <Box minWidth="140px"><TextField label="Discount %" type="number" autoComplete="off" value={discountPercent} onChange={setDiscountPercent} /></Box>
              </InlineStack>

              <TextField label="Bundle title" autoComplete="off" value={title} onChange={setTitle} placeholder="e.g. Widget + Case Bundle" />

              <Box>
                <Button variant="primary" loading={creating} disabled={!canSubmit} onClick={handleCreate}>
                  Create Discount Code
                </Button>
              </Box>

              <Banner tone="info">
                <p>
                  This creates a real Shopify discount that applies once at least {filledProductIds.length || MIN_BUNDLE_PRODUCTS} items
                  across these products are in the cart — Shopify's discount platform can't strictly require "one of
                  each," so a shopper buying that many of just one product would also qualify. This is a real
                  platform limitation, not a bug.
                </p>
              </Banner>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Active Bundle Discounts ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : bundles.length === 0 ? (
                <EmptyState heading="No bundle discounts yet" image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png">
                  <p>Create your first bundle discount above to start encouraging larger orders.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
                  headings={['Title', 'Code', 'Discount', 'Price', 'Actions']}
                  rows={bundles.map((b) => [
                    b.title,
                    <Tooltip content="Click to copy">
                      <Button variant="plain" onClick={() => navigator.clipboard.writeText(b.discount_code)}>{b.discount_code}</Button>
                    </Tooltip>,
                    `${b.discount_percent}%`,
                    b.regular_price != null && b.bundle_price != null
                      ? `$${b.regular_price.toFixed(2)} → $${b.bundle_price.toFixed(2)}`
                      : '—',
                    <Button variant="plain" tone="critical" size="slim" onClick={() => deleteBundle(b.id)}>Delete</Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchBundles(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchBundles(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">One more step: add it to your product pages</Text>
              <Text as="p" tone="subdued">
                Go to <strong>Online Store &rarr; Themes &rarr; Customize</strong>, open a product page, and use{' '}
                <strong>Add block &rarr; Apps &rarr; Bundle Offer</strong>. Shoppers on either product's page will see
                the bundle offer and can add both items with the discount applied automatically at checkout.
              </Text>
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
